// A console tab's side of a RingCentral call's audio (rc-engine.ts delegates WebRTC here). The Web Phone in the worker
// keeps the SIP side; this tab keeps the RTCPeerConnection, the microphone and the speaker for the calls it plays.
// Offers and answers are returned with their ICE candidates gathered (no trickle ICE). A tab asked about a call it
// doesn't have yet (the call's tab went away) sets it up from the context every operation carries.

import { audioDevices } from './audio.ts';
import type { MediaArgs, MediaOp } from './engine.ts';

const GATHER_MS = 5000;

interface Media {
  pc: RTCPeerConnection;
  mic: MediaStream;
  audio: HTMLAudioElement;
}

export class MediaHost {
  private calls = new Map<string, Media>();

  private async media(a: MediaArgs): Promise<Media> {
    const known = this.calls.get(a.callId);
    if (known) return known;
    const pc = new RTCPeerConnection({ iceServers: a.stunServers.map((s) => ({ urls: s.startsWith('stun:') ? s : `stun:${s}` })) });
    const input = audioDevices().input;
    const mic = await navigator.mediaDevices.getUserMedia({ audio: input ? { deviceId: { exact: input } } : true, video: false });
    for (const t of mic.getTracks()) pc.addTrack(t, mic);
    const audio = new Audio();
    audio.autoplay = true;
    pc.ontrack = (e) => {
      audio.srcObject = e.streams[0] ?? new MediaStream([e.track]);
      void audio.play().catch(() => undefined);
    };
    const out = audioDevices().output;
    if (out) await this.sink(audio, out);
    const m = { pc, mic, audio };
    this.calls.set(a.callId, m);
    return m;
  }

  private async sink(audio: HTMLAudioElement, deviceId: string) {
    const el = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
    if (el.setSinkId) await el.setSinkId(deviceId).catch(() => undefined);
  }

  /** The local description once ICE gathering is done (or after GATHER_MS, with what was gathered). */
  private gathered(pc: RTCPeerConnection): Promise<string> {
    return new Promise((resolve) => {
      const done = () => resolve(pc.localDescription?.sdp ?? '');
      if (pc.iceGatheringState === 'complete') return done();
      const t = setTimeout(done, GATHER_MS);
      pc.addEventListener('icegatheringstatechange', () => {
        if (pc.iceGatheringState === 'complete') {
          clearTimeout(t);
          done();
        }
      });
    });
  }

  async handle(op: MediaOp, a: MediaArgs): Promise<unknown> {
    if (op === 'dispose') {
      const m = this.calls.get(a.callId);
      if (m) {
        m.pc.close();
        m.mic.getTracks().forEach((t) => t.stop());
        m.audio.srcObject = null;
        this.calls.delete(a.callId);
      }
      return null;
    }
    const m = await this.media(a);
    switch (op) {
      case 'createOffer': {
        await m.pc.setLocalDescription(await m.pc.createOffer({ iceRestart: !!a.iceRestart }));
        return this.gathered(m.pc);
      }
      case 'createAnswer': {
        await m.pc.setRemoteDescription({ type: 'offer', sdp: a.sdp ?? '' });
        await m.pc.setLocalDescription(await m.pc.createAnswer());
        return this.gathered(m.pc);
      }
      case 'applyAnswer':
        await m.pc.setRemoteDescription({ type: 'answer', sdp: a.sdp ?? '' });
        return null;
      case 'changeInputDevice': {
        const s = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: a.deviceId ?? '' } }, video: false });
        const track = s.getAudioTracks()[0];
        const sender = m.pc.getSenders().find((x) => x.track?.kind === 'audio');
        if (track && sender) await sender.replaceTrack(track);
        m.mic.getTracks().forEach((t) => t.stop());
        m.mic = s;
        return null;
      }
      case 'changeOutputDevice':
        await this.sink(m.audio, a.deviceId ?? '');
        return null;
      case 'setMuted':
        m.mic.getAudioTracks().forEach((t) => (t.enabled = !a.muted));
        return null;
      case 'sendDtmf': {
        const sender = m.pc.getSenders().find((x) => x.track?.kind === 'audio');
        sender?.dtmf?.insertDTMF(a.tones ?? '', a.duration, a.interToneGap);
        return null;
      }
    }
    return null;
  }
}
